import { FormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Revisione53State } from '../../../../store/revisione53.reducers';
import { addYears, isBefore } from 'date-fns';
import { cloneDeep, isEqual } from 'lodash-es';
import { updateVerbaleOnStore } from '@core/helpers/create-validator.helper';
import { getControl, getFormArray, getGroup } from '@shared/helpers/form.helper';
import { isNilOrEmpty, toBoolean } from '@shared/helpers/utils.helper';
import { toNum } from '@shared/helpers/math.helper';
import { MyAllElemCollegioSindacaleEntity, MyAllElemRevisoriControlloContabileEntity, MyVerbale } from '../../../../models/interfaces/revisione.interface';
import { formPaths } from '@shared/constants/form-paths.const';
import { CkNormeValuesEnum } from '../consts/ckNormeValues.enum';

export const controlloCheckOrgControllo = (form: FormGroup, store: Store<Revisione53State>, formCollSind: MyAllElemCollegioSindacaleEntity[] = []) => {
    const amministrazioneControlloGroup = getGroup(form, formPaths.ammControllo);

    const controlloRevLegaliControl = getControl(amministrazioneControlloGroup, 'my_ControlloRevLegali');

    const controlloSociControl = getControl(amministrazioneControlloGroup, 'my_ControlloSoci');

    const componentiCollegioSindacale = Array.isArray(formCollSind) ? formCollSind : [];

    let numeroRevisoriLegaliOrganoControllo = 0;
    let numeroSociOrganoControllo = 0;

    componentiCollegioSindacale.forEach((componenteCollegioSindacale) => {
        const isRevisoreLegale = toBoolean(componenteCollegioSindacale?.my_AllCollSindCkRevisore) === true || componenteCollegioSindacale?.my_AllCollSindCkRevisore === '1';

        const isSocio = toBoolean(componenteCollegioSindacale?.my_AllCollSindCkSocio) === true || componenteCollegioSindacale?.my_AllCollSindCkSocio === '1';

        if (isRevisoreLegale) {
            numeroRevisoriLegaliOrganoControllo++;
        }

        if (isSocio) {
            numeroSociOrganoControllo++;
        }
    });

    controlloRevLegaliControl.setValue(numeroRevisoriLegaliOrganoControllo, {
        emitEvent: false,
    });

    controlloSociControl.setValue(numeroSociOrganoControllo, {
        emitEvent: false,
    });

    updateVerbaleOnStore(store, (myVerbale) => {
        const myVerbaleClone: MyVerbale = cloneDeep(myVerbale);

        myVerbaleClone.my_SezioneI__Rilevazione.my_AmministrazioneControllo.my_ControlloRevLegali = numeroRevisoriLegaliOrganoControllo.toString(10);

        myVerbaleClone.my_SezioneI__Rilevazione.my_AmministrazioneControllo.my_ControlloSoci = numeroSociOrganoControllo.toString(10);

        return myVerbaleClone;
    });
};

export const controlloSocioOrganoControlloRevisoreLegale = (
    form: FormGroup,
    store: Store<Revisione53State>,
    formCollSind: MyAllElemCollegioSindacaleEntity[] = [],
    formRevLegali: MyAllElemRevisoriControlloContabileEntity[] = []
) => {
    const componentiCollegioSindacale = Array.isArray(formCollSind) ? formCollSind : [];
    const revisoriLegali = Array.isArray(formRevLegali) ? formRevLegali : [];

    const codiciFiscaliRevisori = new Set<string>();

    revisoriLegali.forEach((revisoreLegale) => {
        const codiceFiscaleRevisore = revisoreLegale?.my_AllRevisoriControlloContabileCodFisc;

        if (!isNilOrEmpty(codiceFiscaleRevisore)) {
            codiciFiscaliRevisori.add(String(codiceFiscaleRevisore).toUpperCase());
        }
    });

    let numeroSociRevisoriLegali = 0;

    componentiCollegioSindacale.forEach((componenteCollegioSindacale) => {
        const isSocio = toBoolean(componenteCollegioSindacale?.my_AllCollSindCkSocio) === true || componenteCollegioSindacale?.my_AllCollSindCkSocio === '1';

        if (!isSocio) {
            return;
        }

        const codiceFiscaleComponente = componenteCollegioSindacale?.my_AllCollSindCodFisc;

        if (!isNilOrEmpty(codiceFiscaleComponente) && codiciFiscaliRevisori.has(String(codiceFiscaleComponente).toUpperCase())) {
            numeroSociRevisoriLegali++;
        }
    });

    const controlloRevisoreContabileSocioControl = getControl(form, `${formPaths.ammControllo}.my_ControlloRevisoreContabileSocio`);

    controlloRevisoreContabileSocioControl.patchValue(numeroSociRevisoriLegali, {
        emitEvent: false,
    });

    updateVerbaleOnStore(store, (myVerbale) => {
        const myVerbaleClone: MyVerbale = cloneDeep(myVerbale);

        myVerbaleClone.my_SezioneI__Rilevazione.my_AmministrazioneControllo.my_ControlloRevisoreContabileSocio = numeroSociRevisoriLegali.toString(10);

        return myVerbaleClone;
    });
};

/* Normalizza il formato numerico dei campi soci/soci finanziatori (es. "007" -> "7").
Il ricalcolo dei flag Coop9soci/Coop20soci per le 4 norme NON vive più qui: è responsabilità
esclusiva dei listener reattivi in coop-soci-listener.helper.ts (registrati per ciascuna norma
indipendentemente da quale sia attiva) — la duplicazione qui produceva ricalcoli ridondanti
con timing diverso sugli stessi campi, vedi REM-06. */
const SOCI_SCHEDA_AMM_CTRL_FIELDS = ['my_SociFisCoopData', 'my_SociGiuCoopData', 'my_SociSpecData', 'my_SociFisFinData', 'my_SociGiuFinData'] as const;

export const aggiornaSociSchedaAmmCtrl = (store: Store<Revisione53State>) => {
    updateVerbaleOnStore(store, (myVerbale) => {
        const v: MyVerbale = cloneDeep(myVerbale);

        const ebs = v.my_SezioneI__Rilevazione.my_EffettivitaBaseSociale;

        SOCI_SCHEDA_AMM_CTRL_FIELDS.forEach((field) => {
            if (isNilOrEmpty(ebs[field])) {
                return;
            }

            ebs[field] = (toNum(ebs[field]) ?? 0).toString(10);
        });

        return v;
    });
};

export const calcolaScadenzaOrganoAmministrativo = (form: FormGroup, store: Store<Revisione53State>) => {
    const amministrazioneControlloGroup = getGroup(form, formPaths.ammControllo);

    const ckScadenzaOrgAmmControl = getControl(amministrazioneControlloGroup, 'my_CkScadenzaOrgAmm');

    const ckNormeControl = getControl(amministrazioneControlloGroup, 'my_CkNorme');

    const componentiConsiglioAmministrazioneArray = getFormArray(amministrazioneControlloGroup, 'my_AllConsiglioAmministrazione.my_AllElemConsiglioAmministrazione');

    const vistaNormativa = ckNormeControl?.value;

    let valoreScadenzaOrganoAmministrativo = '-1';

    updateVerbaleOnStore(store, (myVerbale) => {
        const myVerbaleClone: MyVerbale = cloneDeep(myVerbale);

        const amministrazioneControllo = myVerbaleClone.my_SezioneI__Rilevazione.my_AmministrazioneControllo;

        let eserciziAScadenza: number | null = null;

        switch (vistaNormativa) {
            case '1':
                eserciziAScadenza = toNum(amministrazioneControllo.my_NormeSrl.my_SrlScadenzaAmministratori);
                break;

            case '2':
                eserciziAScadenza = 99;
                break;

            case '3':
                eserciziAScadenza = toNum(amministrazioneControllo.my_NormeSpaDualistico.my_SpdScadenzaAmministratori);
                break;

            case '4':
                eserciziAScadenza = toNum(amministrazioneControllo.my_NormeSpaMonistico.my_SpmScadenzaAmministratori);
                break;

            default:
                eserciziAScadenza = null;
                break;
        }

        const dataApprovazioneRaw = myVerbaleClone.my_SezioneI__Rilevazione.my_DatiBilancio.my_DataApprovazione2;

        const dataApprovazione = isNilOrEmpty(dataApprovazioneRaw) ? null : new Date(dataApprovazioneRaw);

        valoreScadenzaOrganoAmministrativo = '-1';

        if (eserciziAScadenza && eserciziAScadenza !== 99 && dataApprovazione) {
            for (const componenteConsiglioAmministrazioneControl of componentiConsiglioAmministrazioneArray.controls) {
                const dataElezioneRaw = componenteConsiglioAmministrazioneControl.get('my_AllConsAmmDataElezione')?.value;

                if (isNilOrEmpty(dataElezioneRaw)) {
                    continue;
                }

                const dataElezione = new Date(dataElezioneRaw);
                const dataScadenza = addYears(dataElezione, eserciziAScadenza);

                const organoAmministrativoScaduto = isBefore(dataScadenza, dataApprovazione) || isEqual(dataScadenza, dataApprovazione);

                if (organoAmministrativoScaduto) {
                    valoreScadenzaOrganoAmministrativo = '1';
                    break;
                }
            }
        }

        amministrazioneControllo.my_CkScadenzaOrgAmm = valoreScadenzaOrganoAmministrativo;

        return myVerbaleClone;
    });

    ckScadenzaOrgAmmControl?.patchValue(valoreScadenzaOrganoAmministrativo, {
        emitEvent: false,
    });
};

/**
 * Auto-imposta CkSrlTipoAmministrazione/CkSpoTipoAmministrazione al primo popolamento dati,
 * replicando CaricaRepTableOrgani (VBS): se il campo è vuoto e uno solo tra Consiglio di
 * Amministrazione/Amministratore Unico/Liquidatori risulta popolato, deriva la scelta da lì.
 * Nessuna azione se 0 o 2+ organi risultano popolati contemporaneamente (stato incoerente,
 * gestito dalla validazione esistente) o se la norma non è quella attiva.
 */
export const autoSetTipoAmministrazioneFromRecordCount = (form: FormGroup, store: Store<Revisione53State>, myVerbale: MyVerbale, norma: 'srl' | 'spo'): void => {
    const ammControllo = myVerbale.my_SezioneI__Rilevazione.my_AmministrazioneControllo;
    const normaCkNormeValue = norma === 'srl' ? CkNormeValuesEnum.normeSrl : CkNormeValuesEnum.normeSpaOrdinario;

    if (ammControllo.my_CkNorme !== normaCkNormeValue) {
        return;
    }

    const currentValue = norma === 'srl' ? ammControllo.my_NormeSrl.my_CkSrlTipoAmministrazione : ammControllo.my_NormeSpaOrdinario.my_CkSpoTipoAmministrazione;

    if (!isNilOrEmpty(currentValue)) {
        return;
    }

    const populatedValues = [
        !isNilOrEmpty(ammControllo.my_AllConsiglioAmministrazione?.my_AllElemConsiglioAmministrazione) ? '1' : null,
        !isNilOrEmpty(ammControllo.my_AllAmminUnico) ? '2' : null,
        !isNilOrEmpty(ammControllo.my_AllLiquidatori?.my_AllElemLiquidatori) ? '3' : null,
    ].filter((value): value is string => value !== null);

    if (populatedValues.length !== 1) {
        return;
    }

    const [value] = populatedValues;
    const ckControlPath = norma === 'srl' ? 'my_NormeSrl.my_CkSrlTipoAmministrazione' : 'my_NormeSpaOrdinario.my_CkSpoTipoAmministrazione';

    getControl(getGroup(form, formPaths.ammControllo), ckControlPath).patchValue(value, { emitEvent: false });

    updateVerbaleOnStore(store, (verbale) => {
        const verbaleClone: MyVerbale = cloneDeep(verbale);
        const clonedAmmControllo = verbaleClone.my_SezioneI__Rilevazione.my_AmministrazioneControllo;

        if (norma === 'srl') {
            clonedAmmControllo.my_NormeSrl.my_CkSrlTipoAmministrazione = value;
        } else {
            clonedAmmControllo.my_NormeSpaOrdinario.my_CkSpoTipoAmministrazione = value;
        }

        return verbaleClone;
    });
};
